import { useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useAppDispatch, useAppSelector } from '../../store/Hooks';
import { enmExportStatus } from '../../models/enums/ExportStatus';
import { enmPdfRendererType } from '../../models/enums/PdfRenderer';
import _ from 'lodash';
import ReservationVoucher from '../pdf/ReservationVoucher';
import { setPdfExportObject } from '../../store/SiteSlice';
import PDFTable from '../pdf/PDFTable';
import { ReservationEntryList } from '../pdf/ReservationEntryList';
import { ActionTemplate } from '../pdf/ActionTemplate';
import { StopFreeSaleExportTemplate } from '../stop-free-sale/StopFreeSaleExportTemplate';
import * as  FactSheetTemplate from '../fact-sheet/templates';
import { enmPrintOrientationType } from '../../models/enums/PrintOrientationType';
import { FactSheetGiata } from '../fact-sheet/FactSheetGiata';
import ReservationListTemplate from '../pdf/ReservationListTemplate';
import { message } from 'antd';

const packIntoPages = (groupStarts: number[], pageCapacity: number, totalHeight: number): number[] => {
    const candidates = [...groupStarts, totalHeight];
    const pageStarts = [0];
    let i = 0;
    while (i < candidates.length) {
        const pageStart = pageStarts[pageStarts.length - 1];
        let pageEnd = pageStart;
        while (i < candidates.length && candidates[i] - pageStart <= pageCapacity) {
            pageEnd = candidates[i];
            i++;
        }
        if (pageEnd === pageStart) {
            // Tek bir grup bile bir sayfadan uzun; güvenli bir kesim noktası yok.
            pageEnd = candidates[i];
            i++;
        }
        if (pageEnd < totalHeight) {
            pageStarts.push(pageEnd);
        }
    }
    return pageStarts;
};

const PDFRenderer = () => {
    const dispatch = useAppDispatch();
    const exportStatus = useAppSelector(x => x.site.exportStatus);
    const printStatus = useAppSelector(x => x.site.printStatus);
    const rendererProps = useAppSelector(x => x.site.pdfExportObject);
    const pdfRef = useRef<HTMLDivElement>(null);
    const pageBreakClass = 'page-break';
    useEffect(() => {
        if (exportStatus == enmExportStatus.REQUESTED) {
            if (rendererProps?.rendererType === enmPdfRendererType.ReservationList) {
                setTimeout(() => generatePdfV2((pdf: jsPDF) => pdf.save(`${rendererProps?.params?.name ?? "download"}.pdf`)), 200);
            }
            else if (rendererProps?.rendererType === enmPdfRendererType.EntryList) {
                setTimeout(() => generatePdfHeavyData((pdf: jsPDF) => pdf.save(`${rendererProps?.params?.name ?? "download"}.pdf`)), 200);
            }
            else {
                setTimeout(exportToPDF, 200);
            }
        }
        if (printStatus == enmExportStatus.REQUESTED) {
            setTimeout(printInNewTab, 200);
        }
    }, [rendererProps, exportStatus, printStatus]);


    const testPdf = () => {
        const doc = new jsPDF({
            format: 'a4',
            unit: 'px',
        });

        // Adding the fonts.
        doc.setFont('Inter-Regular', 'normal');

        doc.html(pdfRef.current!, {
            async callback(doc) {
                await doc.save('document');
            },
        });
    }

    const printInNewTab = () => {
        if (!pdfRef.current) return;

        const content = pdfRef.current.innerHTML;

        const styleLinks = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))
            .map((link) => `<link rel="stylesheet" href="${link.href}" />`)
            .join('\n');

        const styleElements = Array.from(document.querySelectorAll('style'))
            .map((style) => style.outerHTML)
            .join('\n');        

        const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <base href="${window.location.origin}/" />
  ${styleLinks}
  ${styleElements}
  <style>
    @page { margin: 10mm; }
    body { margin: 0; padding: 0; background: #fff; }
    .page-break { break-before: page; page-break-before: always; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>${content}</body>
</html>`;

        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const blobUrl = URL.createObjectURL(blob);
        const printWindow = window.open(blobUrl, '_blank');

        if (!printWindow) {
            message.error('Pop-up engellenmiş. Lütfen bu site için pop-up\'a izin verin.');
            URL.revokeObjectURL(blobUrl);
            dispatch(setPdfExportObject(undefined));
            return;
        }

        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
        };

        printWindow.onafterprint = () => {
            URL.revokeObjectURL(blobUrl);
            printWindow.close();
        };

        dispatch(setPdfExportObject(undefined));
    };

    const exportToPDF = async () => {
        generatePdf((pdf: jsPDF) => {
            // Save the PDF
            pdf.save(`${rendererProps?.params?.name ?? "download"}.pdf`);
        })
    }
    /*
    const generatePdf = async (fnCompleted: Function) => {
        if (pdfRef.current) {
            const childNodes = pdfRef.current;
            if (childNodes) {
                const margin = 10; // Margin in mm
                const a4Width = 210; // A4 width in mm
                const a4Height = 297; // A4 height in mm
                const pageHeight = a4Height - 2 * margin; // Usable page height after margins

                const elementToPrint = childNodes as HTMLElement;
                const originalWidth = elementToPrint.scrollWidth;
                const originalHeight = elementToPrint.scrollHeight;

                // ** Set DPI quality for canvas image**
                const scaleFactor = 3; // (1.5 - 3)

                // Generate canvas from the element
                const canvas = await html2canvas(elementToPrint, {
                    scale: scaleFactor, // Increase the scale for better quality
                    useCORS: true,
                    allowTaint: false,
                });

                const imgWidth = a4Width - 2 * margin;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                const totalPages = Math.ceil(imgHeight / pageHeight); // Calculate the number of pages required

                let pdf = new jsPDF({
                    orientation: "p",
                    unit: "mm",
                    format: "a4"
                });

                let remainingHeight = canvas.height;
                let currentPosition = 0;

                for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
                    if (pageIndex > 0) {
                        pdf.addPage(); // Add a new page for every subsequent page
                    }

                    // Create a temporary canvas to slice the original canvas
                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.width = canvas.width;
                    tempCanvas.height = Math.min(pageHeight * (canvas.width / imgWidth), remainingHeight);

                    const tempContext = tempCanvas.getContext('2d');
                    if (tempContext) {
                        // Draw the current portion of the original canvas onto the temporary canvas
                        tempContext.drawImage(
                            canvas,
                            0,
                            currentPosition,
                            canvas.width,
                            tempCanvas.height,
                            0,
                            0,
                            tempCanvas.width,
                            tempCanvas.height
                        );

                        // Add the image to the PDF
                        const imgData = tempCanvas.toDataURL('image/png', 1.0); // 1.0 = Max quality

                        pdf.addImage(
                            imgData,
                            'PNG',
                            margin,
                            margin,
                            imgWidth,
                            (tempCanvas.height * imgWidth) / tempCanvas.width,
                            undefined,
                            'FAST'
                        );

                        // Update remaining height and current position
                        remainingHeight -= tempCanvas.height;
                        currentPosition += tempCanvas.height;
                    }
                }

                //Completed Function
                fnCompleted && fnCompleted(pdf);
            }
        }
        dispatch(setPdfExportObject(undefined));
    };
    */

    const generatePdf = async (fnCompleted: (pdf: jsPDF) => void) => {
        if (!pdfRef.current) return;

        const margin = 10; // mm
        const a4Width = 210;
        const a4Height = 297;
        const pageW = a4Width - margin * 2;
        const pageH = a4Height - margin * 2;

        const root = pdfRef.current as HTMLElement;

        const blocks = Array.from(root.querySelectorAll<HTMLElement>(".restopdf"));

        const targets = blocks.length > 0 ? blocks : [root];

        const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });

        for (let i = 0; i < targets.length; i++) {
            const el = targets[i];

            const canvas = await html2canvas(el, {
                scale: 2,
                useCORS: true,
                allowTaint: false,
                backgroundColor: "#FFFFFF",
                logging: false,
            });

            const imgData = canvas.toDataURL("image/png", 1.0);

            const imgW = pageW;
            const imgH = (canvas.height * imgW) / canvas.width;

            let drawW = imgW;
            let drawH = imgH;
            if (imgH > pageH) {
                const scaleDown = pageH / imgH;
                drawW = imgW * scaleDown;
                drawH = imgH * scaleDown;
            }

            if (i > 0) {
                pdf.addPage();
            }

            const x = margin + (pageW - drawW) / 2;
            const y = margin;

            pdf.addImage(imgData, "PNG", x, y, drawW, drawH, undefined, "FAST");
        }

        fnCompleted?.(pdf);
        dispatch(setPdfExportObject(undefined));
    };

    const generatePdfHeavyData = async (fnCompleted: Function) => {
        if (pdfRef.current) {
            const childNodes = pdfRef.current;
            if (childNodes) {
                const margin = 5;
                const a4Width = 210;
                const a4Height = 297;
                const pageHeight = a4Height - 2 * margin;

                const elementToPrint = childNodes as HTMLElement;
                const blocks = Array.from(elementToPrint.querySelectorAll<HTMLElement>('.root-pdf'));

                if (blocks.length === 0) {
                    message.error('İçerik bulunamadı');
                    return;
                }

                try {
                    let pdf = new jsPDF({
                        orientation: "p",
                        unit: "mm",
                        format: "a4"
                    });

                    let isFirstPage = true;

                    for (const block of blocks) {
                        // Container'ın orijinal boyutlarını al
                        block.style.paddingLeft = '15px';
                        const originalWidth = block.scrollWidth;
                        const originalHeight = block.scrollHeight;
                        const heightBuffer = 24;
                        const capturedHeight = originalHeight + heightBuffer;

                        const canvas = await html2canvas(block, {
                            scale: 2,
                            useCORS: true,
                            allowTaint: true,
                            logging: false,
                            backgroundColor: '#FFFFFF',
                            imageTimeout: 15000,
                            width: originalWidth + 20, // Genişliği biraz daha artırdım
                            height: capturedHeight,
                            x: -10, // Sol taraftan negatif offset
                            onclone: (clonedDoc) => {
                                const elements = clonedDoc.getElementsByTagName('*');
                                for (let i = 0; i < elements.length; i++) {
                                    const el = elements[i] as HTMLElement;
                                    const style = window.getComputedStyle(el);
                                    if (style.display === 'none') {
                                        el.style.display = 'block';
                                    }
                                    if (style.visibility === 'hidden') {
                                        el.style.visibility = 'visible';
                                    }
                                    if (style.backgroundColor === 'rgba(0, 0, 0, 0)' ||
                                        style.backgroundColor === 'transparent') {
                                        el.style.backgroundColor = '#FFFFFF';
                                    }
                                }
                            }
                        });

                        const imgWidth = a4Width - margin;
                        const pageHeightPx = pageHeight * (canvas.width / imgWidth);
                        const scale = canvas.height / capturedHeight;

                        const groupStarts = Array.from(block.querySelectorAll<HTMLElement>('.pdf-safe-break'))
                            .map(el => (el.getBoundingClientRect().top - block.getBoundingClientRect().top) * scale);
                        const pageBreaks = packIntoPages(groupStarts, pageHeightPx, canvas.height);

                        block.style.paddingLeft = '';

                        for (let pageIndex = 0; pageIndex < pageBreaks.length; pageIndex++) {
                            if (!isFirstPage) {
                                pdf.addPage();
                            }
                            isFirstPage = false;

                            const sliceStart = pageBreaks[pageIndex];
                            const sliceHeight = (pageBreaks[pageIndex + 1] ?? canvas.height) - sliceStart;

                            const tempCanvas = document.createElement('canvas');
                            tempCanvas.width = canvas.width;
                            tempCanvas.height = sliceHeight;

                            const tempContext = tempCanvas.getContext('2d', { alpha: false });
                            if (tempContext) {
                                tempContext.fillStyle = '#FFFFFF';
                                tempContext.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

                                tempContext.drawImage(
                                    canvas,
                                    0,
                                    sliceStart,
                                    canvas.width,
                                    sliceHeight,
                                    0,
                                    0,
                                    tempCanvas.width,
                                    tempCanvas.height
                                );

                                const imgData = tempCanvas.toDataURL('image/jpeg', 1.0);
                                pdf.addImage(
                                    imgData,
                                    'JPEG',
                                    0, // Sol margin'i 0'a indirdim
                                    margin,
                                    imgWidth,
                                    (sliceHeight * imgWidth) / canvas.width,
                                    undefined,
                                    'FAST'
                                );

                                tempCanvas.width = 0;
                                tempCanvas.height = 0;
                            }
                        }

                        canvas.width = 0;
                        canvas.height = 0;
                    }

                    fnCompleted && fnCompleted(pdf);
                } catch (error) {
                    console.error('PDF oluşturma hatası:', error);
                    message.error('PDF oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
                }
            }
            dispatch(setPdfExportObject(undefined));
        }
    };

    const generatePdfV2 = async (fnCompleted: Function) => {
        if (pdfRef.current) {
            const childNodes = pdfRef.current;
            if (childNodes) {
                const margin = 10; // Margin in mm
                const a4Width = 210; // A4 width in mm
                const a4Height = 297; // A4 height in mm
                const pageHeight = a4Height - 2 * margin; // Usable page height after margins

                const elementToPrint = childNodes as HTMLElement;
                const containerFluidElement = elementToPrint.querySelector('.container-fluid');

                if (!containerFluidElement) {
                    return;
                }

                const children = Array.from(containerFluidElement.children);

                var orientation: "p" | "l" = "p";
                if (rendererProps?.orientation) {
                    switch (rendererProps.orientation) {
                        case enmPrintOrientationType.LANDSCAPE:
                            orientation = "l";
                            break;
                        default:
                            orientation = "p";
                            break;
                    }
                }

                let pdf = new jsPDF({
                    orientation: orientation,
                    unit: 'mm',
                    format: 'a4'
                });

                // Her child için işlem yap
                for (let i = 0; i < children.length; i++) {
                    const child = children[i];

                    if (i > 0) {
                        pdf.addPage();
                    }

                    // Child element için canvas oluştur
                    const canvas = await html2canvas(child as HTMLElement, {
                        scale: 2,
                        useCORS: true,
                        allowTaint: false,
                    });

                    const imgWidth = a4Width - 2 * margin;
                    const imgHeight = (canvas.height * imgWidth) / canvas.width;
                    const totalPages = Math.ceil(imgHeight / (pageHeight)); // Calculate the number of pages required

                    let remainingHeight = canvas.height;
                    let currentPosition = 0;

                    // Her sayfa için canvas parçası oluştur
                    for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
                        if (pageIndex > 0) {
                            pdf.addPage();
                        }

                        // Geçici canvas oluştur
                        const tempCanvas = document.createElement('canvas');
                        const tempContext = tempCanvas.getContext('2d');

                        if (tempContext) {
                            // Mevcut sayfaya sığacak yüksekliği hesapla
                            const pageHeightInPx = (pageHeight * canvas.width) / imgWidth;
                            tempCanvas.width = canvas.width;
                            tempCanvas.height = Math.min(pageHeightInPx, remainingHeight);

                            // Orijinal canvas'tan parça kopyala
                            tempContext.drawImage(
                                canvas,
                                0,
                                currentPosition,
                                canvas.width,
                                tempCanvas.height,
                                0,
                                0,
                                tempCanvas.width,
                                tempCanvas.height
                            );

                            // PDF'e ekle
                            const partialHeight = (tempCanvas.height * imgWidth) / tempCanvas.width;
                            pdf.addImage(
                                tempCanvas.toDataURL('image/png'),
                                'PNG',
                                margin,
                                margin,
                                imgWidth,
                                partialHeight,
                                undefined,
                                'FAST'
                            );

                            // Pozisyonu güncelle
                            remainingHeight -= tempCanvas.height;
                            currentPosition += tempCanvas.height;
                        }
                    }
                }

                // Completed Function
                fnCompleted && fnCompleted(pdf);
            }
            dispatch(setPdfExportObject(undefined));
        }
    };

    const getRendererObject = () => {
        switch (rendererProps?.rendererType) {
            case enmPdfRendererType.ReservationVoucher:
                return <ReservationVoucher {...rendererProps.params} />
            case enmPdfRendererType.EntryList:
                return <ReservationEntryList {...rendererProps.params} />
            case enmPdfRendererType.ActionTemplate:
                return <ActionTemplate {...rendererProps.params} />
            case enmPdfRendererType.PDFTable:
                return <PDFTable {...rendererProps.params} />
            case enmPdfRendererType.StopFreeSaleTemplate:
                return <StopFreeSaleExportTemplate {...rendererProps.params} />
            case enmPdfRendererType.FactSheetTemplateCity:
                return <FactSheetTemplate.CityTemplate {...rendererProps.params} />
            case enmPdfRendererType.FactSheetTemplateLuxury:
                return <FactSheetTemplate.LuxuryTemplate {...rendererProps.params} />
            case enmPdfRendererType.FactSheetTemplateResort:
                return <FactSheetTemplate.ResortTemplate {...rendererProps.params} />
            case enmPdfRendererType.FactSheetGiata:
                return <FactSheetGiata {...rendererProps.params} />
            case enmPdfRendererType.ReservationList:
                return <ReservationListTemplate {...rendererProps.params} />
        }
    }

    return (
        <>
            {
                rendererProps && rendererProps.rendererType
                &&
                <div style={{
                    position: 'absolute',
                    top: '-9999px',
                    left: '-9999px',
                }}>
                    {
                        <div ref={pdfRef}>
                            {
                                getRendererObject()
                            }
                        </div>
                    }
                </div>
            }
        </>
    )
}

export { PDFRenderer };